import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { useFormDraft } from '@/hooks/useFormDraft';

const BUDGET_CATEGORIES = ['food', 'transport', 'housing', 'entertainment', 'health', 'education', 'shopping', 'services', 'other'];

export default function AddBudgetModal({ open, onClose, budget }) {
  const { t } = useTranslation();
  const addBudget = useStore((s) => s.addBudget);
  const updateBudget = useStore((s) => s.updateBudget);
  const existingBudgets = useStore((s) => s.budgets);
  const isEdit = !!budget;
  const draft = useFormDraft('addBudget');

  const [category, setCategory] = useState('');
  const [limit, setLimit] = useState('');
  const [errors, setErrors] = useState({});

  const usedCategories = existingBudgets.map((b) => b.category);
  const available = isEdit
    ? BUDGET_CATEGORIES
    : BUDGET_CATEGORIES.filter((c) => !usedCategories.includes(c));

  const validate = () => {
    const errs = {};
    if (!category) errs.category = t('budgets.form.categoryRequired');
    if (!limit || Number(limit) <= 0) errs.limit = t('budgets.form.limitRequired');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    if (isEdit) {
      updateBudget(budget.id, { limit: Number(limit) });
    } else {
      addBudget({ category, limit: Number(limit), spent: 0 });
    }
    draft.clear();
    handleClose();
  };

  const handleClose = () => {
    setCategory('');
    setLimit('');
    setErrors({});
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    if (budget) {
      setCategory(budget.category);
      setLimit(String(budget.limit));
    } else {
      const saved = draft.load();
      if (saved) {
        setCategory(saved.category || '');
        setLimit(saved.limit || '');
      }
    }
    setErrors({});
  }, [budget, open]);

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ category, limit });
  }, [open, isEdit, category, limit]);

  return (
    <Modal open={open} onClose={handleClose} title={isEdit ? t('common.edit') : t('budgets.addNew')} footer={
      <Button className="w-full" onClick={handleSave} disabled={available.length === 0}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Category (hidden when editing) */}
        {!isEdit && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('transactions.form.category')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {available.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`py-2 px-3 text-xs font-medium rounded-xl border transition-all flex flex-col items-center gap-1 ${
                    category === c
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  <span>{CATEGORY_ICONS[c] || '📌'}</span>
                  {t(`categories.${c}`)}
                </button>
              ))}
            </div>
            {available.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">{t('budgets.form.allCategoriesUsed')}</p>
            )}
            {errors.category && <p className="text-xs text-danger-500 mt-1">{errors.category}</p>}
          </div>
        )}

        {/* Limit */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('budgets.form.monthlyLimit')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={limit}
              onChange={setLimit}
              className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${
                errors.limit ? 'border-danger-500' : ''
              }`}
            />
          </div>
          {errors.limit && <p className="text-xs text-danger-500 mt-1">{errors.limit}</p>}
        </div>

      </div>
    </Modal>
  );
}
